// Utility functions for Google Analytics tracking

// Extend Window interface to include gtag
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, unknown>,
    ) => void;
    dataLayer?: unknown[];
  }
}

const GA_QUEUE_KEY = 'google_analytics_queue';
const MAX_QUEUE_SIZE = 50; // Prevent unbounded queue growth

type QueuedGAEvent = { eventName: string; eventParams?: Record<string, unknown> };

// Helper to get queue from sessionStorage
const getQueue = (): QueuedGAEvent[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const stored = sessionStorage.getItem(GA_QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    // sessionStorage access failed (private browsing, quota exceeded, etc.)
    return [];
  }
};

// Helper to save queue to sessionStorage
const saveQueue = (queue: QueuedGAEvent[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem(GA_QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // sessionStorage write failed (quota exceeded, private browsing, etc.)
    // Events will be lost but this is acceptable for tracking
  }
};

// Helper to clear queue from sessionStorage
const clearQueue = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    sessionStorage.removeItem(GA_QUEUE_KEY);
  } catch {
    // sessionStorage clear failed - not critical
  }
};

export const isGoogleAnalyticsReady = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return typeof window.gtag === 'function' && Array.isArray(window.dataLayer);
};

/**
 * Track a custom event in Google Analytics
 * @param eventName - The name of the event (e.g., 'purchase', 'sign_up')
 * @param eventParams - Additional parameters for the event
 */
export const trackGAEvent = (eventName: string, eventParams?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // If Google Analytics is ready, track immediately
  if (isGoogleAnalyticsReady()) {
    try {
      window.gtag?.('event', eventName, eventParams);
    } catch (error) {
      console.error('[Google Analytics] Failed to track event:', eventName, error);
    }
    return;
  }

  // Otherwise, queue the event for later (persists across page reloads in same session)
  const queue = getQueue();

  // Enforce max queue size to prevent unbounded growth
  if (queue.length >= MAX_QUEUE_SIZE) {
    // Remove oldest event when queue is full
    queue.shift();
  }

  queue.push({ eventName, eventParams });
  saveQueue(queue);
};

/**
 * Process any queued Google Analytics events once GA is ready
 */
export const processGAQueue = (): void => {
  if (typeof window === 'undefined' || !isGoogleAnalyticsReady()) {
    return;
  }

  const queue = getQueue();

  if (queue.length === 0) {
    return;
  }

  // Process all queued events
  for (const { eventName, eventParams } of queue) {
    try {
      window.gtag?.('event', eventName, eventParams);
    } catch (error) {
      console.error('[Google Analytics] Failed to track queued event:', eventName, error);
    }
  }

  // Clear the queue after processing
  clearQueue();
};

/**
 * Track a page view in Google Analytics
 * @param url - The page URL to track
 */
export const trackGAPageView = (url: string): void => {
  if (!isGoogleAnalyticsReady()) {
    return;
  }

  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  if (!measurementId) {
    return;
  }

  try {
    window.gtag?.('config', measurementId, {
      page_path: url,
    });
  } catch (error) {
    console.error('Failed to track Google Analytics page view:', error);
  }
};

/**
 * Set user properties in Google Analytics
 * @param properties - User properties to set
 */
export const setGAUserProperties = (properties: Record<string, unknown>): void => {
  if (!isGoogleAnalyticsReady()) {
    return;
  }

  try {
    window.gtag?.('set', 'user_properties', properties);
  } catch (error) {
    console.error('Failed to set Google Analytics user properties:', error);
  }
};
